import React, { useMemo, useState } from "react"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, localDateString, parseLocalDate } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const tabs = [
	{ id: "overview", label: "Итоги", icon: "uil-apps" },
	{ id: "branches", label: "Филиалы", icon: "uil-store" },
	{ id: "sales", label: "Продажи", icon: "uil-receipt" },
	{ id: "actions", label: "Сотрудники", icon: "uil-users-alt" },
]

function startOfWeekMonday(date) {
	const copy = new Date(date)
	const day = copy.getDay() || 7
	copy.setDate(copy.getDate() - day + 1)
	copy.setHours(0, 0, 0, 0)
	return copy
}

function addDays(date, days) {
	const copy = new Date(date)
	copy.setDate(copy.getDate() + days)
	return copy
}

function yesterdayDate() {
	const date = new Date()
	date.setHours(0, 0, 0, 0)
	return addDays(date, -1)
}

// Последняя полностью закрытая неделя (как по умолчанию на сервере)
function lastFullWeekStart() {
	return addDays(startOfWeekMonday(addDays(yesterdayDate(), 1)), -7)
}

// Последняя неделя, которую можно выбрать: текущая, если в ней уже есть закрытый день
function lastSelectableWeekStart() {
	return startOfWeekMonday(yesterdayDate())
}

function isoWeekNumber(date) {
	const copy = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
	const day = copy.getUTCDay() || 7
	copy.setUTCDate(copy.getUTCDate() + 4 - day)
	const yearStart = new Date(Date.UTC(copy.getUTCFullYear(), 0, 1))
	return Math.ceil(((copy - yearStart) / 86400000 + 1) / 7)
}

function formatShortDate(date) {
	return `${String(date.getDate()).padStart(2, "0")}.${String(date.getMonth() + 1).padStart(2, "0")}`
}

function formatFullDate(dateString) {
	if (!dateString) return ""
	const date = parseLocalDate(dateString)
	return `${formatShortDate(date)}.${date.getFullYear()}`
}

function formatComparePeriod(startDateString, endDateString) {
	if (!startDateString || !endDateString) return ""
	return `${formatFullDate(startDateString)} – ${formatFullDate(endDateString)}`
}

function formatPercent(value) {
	return `${Number(value || 0).toFixed(1).replace(".0", "")}%`
}

// unit === "pp" — процентная метрика, рост в процентных пунктах
function formatGrowth(value, unit) {
	if (value === null || value === undefined) return unit === "pp" ? "—" : "новое"
	const sign = Number(value) >= 0 ? "+" : ""
	const number = Number(value).toFixed(1).replace(".0", "")
	return unit === "pp" ? `${sign}${number} п.п.` : `${sign}${number}%`
}

function formatMetric(value) {
	const number = Number(value || 0)
	return formatMoney(number, 0)
}

function metricActual(metric) {
	return metric && typeof metric === "object" ? metric.actual : metric
}

function shortMetric(shortValue, value) {
	// Полное число из value; готовая строка с бэка — только если числа нет
	if (value !== null && value !== undefined) return formatMetric(value)
	return shortValue || formatMetric(0)
}

// inverse — рост метрики плохой (food cost)
function GrowthValue({ value, unit, inverse = false }) {
	if (value === null || value === undefined) return <span className="text-muted">{formatGrowth(value, unit)}</span>
	const isPositive = Number(value) >= 0
	const isGood = inverse ? !isPositive : isPositive
	return (
		<span className={isGood ? "text-success" : "text-danger"} style={{ whiteSpace: "nowrap" }}>
			<span style={{ fontSize: "13px" }}>{isPositive ? "↗" : "↘"}</span> {formatGrowth(value, unit)}
		</span>
	)
}

function RankBadge({ rank }) {
	const colors = {
		1: { color: "var(--dark-fg-warning, #b7791f)", background: "var(--dark-tint-warning, #fff7d6)", border: "var(--dark-tint-border-warning, #f4d06f)" },
		2: { color: "var(--dark-text-muted, #64748b)", background: "var(--dark-surface-2, #f1f5f9)", border: "var(--dark-tint-border-primary, #cbd5e1)" },
		3: { color: "var(--dark-fg-warning, #b45309)", background: "var(--dark-tint-warning, #fff1df)", border: "var(--dark-tint-border-warning, #f6c28b)" },
	}
	const style = colors[rank] || { color: "var(--dark-text-muted, #6b7280)", background: "transparent", border: "transparent" }

	return (
		<span
			className="d-inline-flex align-items-center justify-content-center fw-semibold"
			style={{ width: "24px", height: "24px", borderRadius: "50%", color: style.color, background: style.background, border: `1px solid ${style.border}`, fontSize: "13px" }}
		>
			{rank}
		</span>
	)
}

function SectionCard({ title, subtitle, children, actions = null, titleIcon = null }) {
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className={`d-flex justify-content-between align-items-start gap-3 ${title || subtitle || actions ? "mb-3" : ""}`}>
					<div>
						{title && (
							<h5 className="mb-1 d-flex align-items-center gap-2">
								{titleIcon}
								{title}
							</h5>
						)}
						{subtitle && <div style={mutedStyle}>{subtitle}</div>}
					</div>
					{actions}
				</div>
				{children}
			</div>
		</div>
	)
}

function KpiCard({ title, value, metric, icon, compareLabel, inverse = false }) {
	const growth = metric?.growth_percent
	const unit = metric?.growth_unit
	const isNew = growth === null || growth === undefined
	const isGood = inverse ? Number(growth) < 0 : Number(growth) >= 0
	return (
		<div className="card mb-3 h-100" style={cardStyle}>
			<div className="card-body py-3 px-4">
				<div className="d-flex align-items-start gap-2 h-100">
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px", lineHeight: "20px" }}></i>
					<div className="d-flex flex-column justify-content-between">
						<div style={mutedStyle}>{title}</div>
						<h4 className="mb-1">{value}</h4>
						<div className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
							<span
								className={isNew ? "text-muted" : isGood ? "text-success" : "text-danger"}
								style={{ background: isNew ? "var(--dark-surface-2, #f1f4f8)" : isGood ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)", borderRadius: "14px", padding: "2px 8px", whiteSpace: "nowrap" }}
							>
								{formatGrowth(growth, unit)}
							</span>
							{compareLabel && <span className="text-muted">{compareLabel}</span>}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function IndicatorCard({ title, value, metric, subtitle, inverse = false }) {
	return (
		<div className="col-xl-3 col-md-6">
			<div className="border rounded-3 p-3 h-100">
				<div style={mutedStyle}>{title}</div>
				<h4 className="mb-1">{value}</h4>
				{metric && <GrowthValue value={metric.growth_percent} unit={metric.growth_unit} inverse={inverse} />}
				{subtitle && <div className="text-muted mt-1" style={{ fontSize: "12px" }}>{subtitle}</div>}
			</div>
		</div>
	)
}

function ProgressLine({ percent, color }) {
	const value = Math.min(Math.max(Number(percent || 0), 0), 100)
	return (
		<div className="progress" style={{ height: "9px", borderRadius: "20px" }}>
			<div className="progress-bar" style={{ width: `${value}%`, background: color, borderRadius: "20px" }}></div>
		</div>
	)
}

function planColor(percent) {
	if (percent === null || percent === undefined) return "#c4c6ca"
	const value = Number(percent || 0)
	if (value >= 100) return "#1fbf87"
	if (value >= 80) return "#f5a623"
	return "#e55353"
}

function PlanSummaryMetric({ title, value, percent, plan }) {
	const color = planColor(percent)

	return (
		<div className="col-md-4">
			<div className="p-3 h-100" style={{ border: "1px solid var(--dark-border, #e5eaf1)", borderRadius: "12px", background: "var(--dark-surface-2, #f8fafc)" }}>
				<div className="text-muted text-uppercase" style={{ fontSize: "12px" }}>{title}</div>
				<div className="d-flex align-items-end gap-2 mb-2">
					<h3 className="mb-0">{value}</h3>
					<b style={{ color }}>{percent === null ? "—" : formatPercent(percent)}</b>
				</div>
				<ProgressLine percent={percent} color={color} />
				<div className="text-muted small mt-2">план: {plan}</div>
			</div>
		</div>
	)
}

function BranchPlanList({ items = [] }) {
	if (!items.length) {
		return <div className="text-muted">Нет данных</div>
	}

	const withPlan = items.filter(item => Number(item.plan || 0) > 0)
	const totalFact = withPlan.reduce((sum, item) => sum + Number(item.fact || 0), 0)
	const totalPlan = withPlan.reduce((sum, item) => sum + Number(item.plan || 0), 0)
	const totalCheques = withPlan.reduce((sum, item) => sum + Number(item.cheques || 0), 0)
	const totalChequesPlan = withPlan.reduce((sum, item) => sum + Number(item.plan_cheques || 0), 0)
	const averageCheque = totalCheques > 0 ? totalFact / totalCheques : 0
	const averageChequePlan = totalChequesPlan > 0 ? totalPlan / totalChequesPlan : 0
	const revenuePercent = totalPlan > 0 ? totalFact / totalPlan * 100 : null
	const chequesPercent = totalChequesPlan > 0 ? totalCheques / totalChequesPlan * 100 : null
	const averageChequePercent = averageChequePlan > 0 ? averageCheque / averageChequePlan * 100 : null
	const maxPercent = Math.max(...items.map(item => Number(item.execution_percent || 0)), 100)

	return (
		<div>
			<div className="row g-3 mb-4">
				<PlanSummaryMetric title="Выручка" value={formatMetric(totalFact)} percent={revenuePercent} plan={formatMetric(totalPlan)} />
				<PlanSummaryMetric title="Чеки" value={formatMoney(totalCheques)} percent={chequesPercent} plan={formatMoney(Math.round(totalChequesPlan))} />
				<PlanSummaryMetric title="Ср. чек" value={formatMetric(averageCheque)} percent={averageChequePercent} plan={formatMetric(averageChequePlan)} />
			</div>
			<div className="text-muted text-uppercase mb-3" style={{ fontSize: "12px" }}>Филиалы</div>
			{items.map((item, index) => {
				const noPlan = item.status === "no_plan"
				const color = planColor(item.execution_percent)
				const width = noPlan ? 0 : Math.min(Number(item.execution_percent || 0) / maxPercent * 100, 100)

				return (
					<div className="d-flex align-items-center gap-3 mb-3" key={item.pos_id}>
						<div className="text-muted text-end" style={{ width: "24px", fontSize: "13px" }}>{index + 1}</div>
						<div className="text-truncate" style={{ width: "170px" }}>{item.pos_name}</div>
						<div className="flex-grow-1 position-relative" style={{ height: "22px", background: "var(--dark-surface-2, #f6f7f9)", borderRadius: "4px" }}>
							<div style={{ position: "absolute", left: 0, top: 0, height: "22px", width: `${width}%`, background: color, borderRadius: "4px" }}></div>
							<div className={`position-absolute fw-semibold ${width > 30 ? "text-white" : "text-muted"}`} style={{ right: "8px", top: "2px", fontSize: "12px" }}>
								{shortMetric(item.fact_short, item.fact)}
							</div>
						</div>
						<div className="fw-semibold text-end" style={{ width: "74px", color: noPlan ? "var(--dark-text-muted, #8791a1)" : color }}>
							{noPlan ? "нет плана" : formatPercent(item.execution_percent)}
						</div>
					</div>
				)
			})}
		</div>
	)
}

function SimpleTable({ columns, rows, emptyText = "Нет данных", className = "" }) {
	return (
		<div className={`table-responsive ${className}`}>
			<table className="table align-middle mb-0" style={{ fontSize: "15px", borderCollapse: "separate", borderSpacing: 0 }}>
				<thead>
					<tr style={{ border: "none" }}>
						{columns.map(column => (
							<th key={column.key} className={`py-3 text-muted fw-semibold ${column.className || ""}`} style={{ border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }}>
								{column.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows?.length > 0 ? rows.map((row, index) => (
						<tr key={row.id || row.product_id || row.group_id || row.pos_id || row.employee_login || row.courier_key || index}>
							{columns.map(column => (
								<td key={column.key} className={`py-3 ${column.className || ""}`} style={{ borderBottom: index === rows.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)" }}>
									{column.render ? column.render(row, index) : row[column.key]}
								</td>
							))}
						</tr>
					)) : (
						<tr>
							<td className="text-center text-muted py-4" colSpan={columns.length}>{emptyText}</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	)
}

function ProductGroupsTable({ rows = [], openedGroups, toggleGroup, className = "" }) {
	if (!rows.length) {
		return <div className="text-center text-muted py-4">Нет данных</div>
	}

	return (
		<div className={`table-responsive ${className}`}>
			<table className="table align-middle mb-0" style={{ fontSize: "15px", borderCollapse: "separate", borderSpacing: 0 }}>
				<thead>
					<tr style={{ border: "none" }}>
						<th className="py-3 text-muted fw-semibold" style={{ border: "none", fontSize: "13px" }}>#</th>
						<th className="py-3 text-muted fw-semibold" style={{ border: "none", fontSize: "13px" }}>Группа блюда</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Выручка</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Δ</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Чеки с группой</th>
						<th className="py-3 text-muted fw-semibold text-end" style={{ border: "none", fontSize: "13px" }}>Δ</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((group, index) => {
						const isOpen = openedGroups.includes(group.group_id)
						return (
							<React.Fragment key={group.group_id || index}>
								<tr style={{ cursor: "pointer" }} onClick={() => toggleGroup(group.group_id)}>
									<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}><RankBadge rank={group.rank} /></td>
									<td className="py-3" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>
										<b className="d-inline-flex align-items-center gap-2">
											<i className={`uil ${isOpen ? "uil-angle-up" : "uil-angle-down"}`}></i>
											{group.group_name}
										</b>
									</td>
									<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{shortMetric(group.revenue_short, group.revenue)}</td>
									<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}><GrowthValue value={group.revenue_growth_percent} /></td>
									<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}>{formatMoney(group.cheques)}</td>
									<td className="py-3 text-end" style={{ borderBottom: "1px solid var(--dark-border, #eef2f6)" }}><GrowthValue value={group.cheques_growth_percent} /></td>
								</tr>
								{isOpen && group.products?.map(product => (
									<tr key={`${group.group_id}-${product.product_id}`}>
										<td className="py-2 text-muted" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)" }}></td>
										<td className="py-2 text-muted" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)", paddingLeft: "42px" }}>
											<span className="d-inline-flex align-items-center gap-2">
												<i className="uil uil-corner-down-right-alt"></i>
												{product.product_name}
											</span>
										</td>
										<td className="py-2 text-end" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)" }}>{shortMetric(product.revenue_short, product.revenue)}</td>
										<td className="py-2 text-end" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)" }}><GrowthValue value={product.revenue_growth_percent} /></td>
										<td className="py-2 text-end" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)" }}>{formatMoney(product.cheques)}</td>
										<td className="py-2 text-end" style={{ borderBottom: "1px solid var(--dark-border, #f2f4f7)" }}><GrowthValue value={product.cheques_growth_percent} /></td>
									</tr>
								))}
							</React.Fragment>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

function CouriersTable({ rows }) {
	return (
		<SimpleTable
			columns={[
				{ key: "rank", label: "#", render: row => <RankBadge rank={row.rank} /> },
				{ key: "courier_name", label: "Курьер", render: row => <b>{row.courier_name}</b> },
				{ key: "pos_name", label: "Филиал", render: row => row.pos_name || "—" },
				{ key: "cheques", label: "Заказы", className: "text-end", render: row => <b>{formatMoney(row.cheques)}</b> },
				{ key: "revenue", label: "Выручка", className: "text-end", render: row => shortMetric(row.revenue_short, row.revenue) },
				{ key: "revenue_growth_percent", label: "Выручка %", className: "text-end", render: row => <GrowthValue value={row.revenue_growth_percent} /> },
			]}
			rows={rows}
			className="mb-3"
		/>
	)
}

function Weekly() {
	const [data, setData] = useState(null)
	const [activeTab, setActiveTab] = useState("overview")
	const [openedGroups, setOpenedGroups] = useState([])

	const filters = useAnalyticsFilters({
		week_start: localDateString(lastFullWeekStart()),
		pos_id: -1,
		order_type_ids: [],
		source_ids: [],
	}, searchWithFilter)
	const { draft, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		setOpenedGroups([])
		const response = await GET(buildAnalyticsUrl("/analytics/weekly", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// 12 последних недель, новые сверху; будущие недели выбрать нельзя
	const weekOptions = useMemo(() => {
		const last = lastSelectableWeekStart()
		return Array.from({ length: 12 }, (_, index) => {
			const start = addDays(last, -7 * index)
			const end = addDays(start, 6)
			const partial = index === 0 && end >= new Date(new Date().setHours(0, 0, 0, 0))
			return {
				id: localDateString(start),
				name: `${formatShortDate(start)} – ${formatShortDate(end)} · ${isoWeekNumber(start)} нед.${partial ? " (текущая)" : ""}`,
			}
		})
	}, [])

	const lastWeekStart = weekOptions[0]?.id
	const currentWeek = parseLocalDate(draft.week_start)

	function shiftWeek(days) {
		const next = localDateString(addDays(currentWeek, days))
		if (next > lastWeekStart) return
		patch({ week_start: next })
	}

	function toggleGroup(groupId) {
		setOpenedGroups(prev => prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId])
	}

	const revenueTrendChart = useMemo(() => ({
		series: data?.overview?.daily_revenue_trend?.series?.map(item => ({ name: item.name, data: item.data || [] })) || [],
		options: {
			chart: { type: "area", height: 300, toolbar: { show: false } },
			colors: ["#d23434", "#c4c6ca"],
			dataLabels: { enabled: false },
			stroke: { curve: "smooth", width: 2 },
			xaxis: { categories: data?.overview?.daily_revenue_trend?.categories || [] },
			yaxis: { labels: { formatter: value => formatMetric(value) } },
			tooltip: { y: { formatter: value => value === null ? "—" : formatMoney(value, 0) } },
			legend: { position: "top", horizontalAlign: "right" },
			fill: { opacity: 0.18 },
		},
	}), [data])

	const chequesTrendChart = useMemo(() => ({
		series: data?.overview?.daily_cheques_trend?.series?.map(item => ({ name: item.name, data: item.data || [] })) || [],
		options: {
			chart: { type: "bar", height: 300, toolbar: { show: false } },
			colors: ["#2f80c9", "#c4c6ca"],
			dataLabels: { enabled: false },
			plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
			xaxis: { categories: data?.overview?.daily_cheques_trend?.categories || [] },
			yaxis: { labels: { formatter: value => formatMoney(value, 0) } },
			tooltip: { y: { formatter: value => value === null ? "—" : formatMoney(value, 0) } },
			legend: { position: "top", horizontalAlign: "right" },
		},
	}), [data])

	const summary = data?.summary || {}
	const status = data?.overview?.status || {}
	const foodCost = data?.sales?.food_cost || {}
	const indicators = data?.sales?.indicators || {}
	const currentPeriodLabel = formatComparePeriod(data?.week_start, data?.period_end)
	const comparePeriodLabel = formatComparePeriod(data?.compare_start, data?.compare_end)
	const compareLabel = data?.compare_label
	const subtitle = currentPeriodLabel
		? `${currentPeriodLabel}${data?.is_partial ? " (неделя не закончилась)" : ""} · сравнение: ${comparePeriodLabel}`
		: ""

	return (
		<>
			<AnalyticsFilterBar
				title="Weekly"
				icon="uil-calendar-alt"
				subtitle={subtitle}
				filters={filters}
				presets={false}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="Неделя" wide>
					<div className="d-flex align-items-center gap-2">
						<button type="button" className="btn af-icon-btn" title="Предыдущая неделя" onClick={() => shiftWeek(-7)}>
							<i className="uil uil-angle-left"></i>
						</button>
						<div className="flex-grow-1" style={{ minWidth: 0 }}>
							<FilterSelect options={weekOptions} value={draft.week_start} onChange={(id) => id !== -1 && patch({ week_start: id })} />
						</div>
						<button type="button" className="btn af-icon-btn" title="Следующая неделя" disabled={draft.week_start >= lastWeekStart} onClick={() => shiftWeek(7)}>
							<i className="uil uil-angle-right"></i>
						</button>
					</div>
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row align-items-stretch mb-3">
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Выручка" value={formatMetric(metricActual(summary.revenue))} metric={summary.revenue} compareLabel={compareLabel} icon="uil-money-bill" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Чеки" value={formatMoney(metricActual(summary.cheques))} metric={summary.cheques} compareLabel={compareLabel} icon="uil-receipt" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Маржа" value={summary.margin_percent ? formatPercent(metricActual(summary.margin_percent)) : "—"} metric={summary.margin_percent} compareLabel={compareLabel} icon="uil-percentage" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Средний чек" value={formatMetric(metricActual(summary.average_cheque))} metric={summary.average_cheque} compareLabel={compareLabel} icon="uil-chart-line" />
				</div>
				<div className="col-xl col-md-6 mb-3">
					<KpiCard title="Глубина чека" value={Number(metricActual(summary.cheque_depth) || 0).toFixed(1)} metric={summary.cheque_depth} compareLabel={compareLabel} icon="uil-layers" />
				</div>
			</div>

			{activeTab === "overview" && (
				<div className="pb-3">
					<SectionCard>
						<h3 className={Number(status.plan || 0) <= 0 ? "" : Number(status.execution_percent || 0) >= 100 ? "text-success" : "text-warning"}>
							{status.title || "Итоги недели"}
						</h3>
						<div className="text-muted">{status.description}</div>
					</SectionCard>

					<SectionCard title="План по каналам" subtitle="План сети разложен по долям каналов в выручке точек за 4 недели до начала недели">
						{data?.overview?.channel_plan?.map(item => {
							const color = Number(item.execution_percent || 0) >= 100 ? "#1fbf87" : "#f5a623"
							const hasPlan = Number(item.plan || 0) > 0
							return (
								<div className="mb-4" key={item.code}>
									<div className="d-flex justify-content-between align-items-center mb-2">
										<div>
											<b>{item.name}</b>
											<div className="text-muted small">
												{shortMetric(item.fact_short, item.fact)}{hasPlan ? ` из ${shortMetric(item.plan_short, item.plan)}` : " · плана нет"}
											</div>
										</div>
										<div className="text-end">
											<div className="text-muted small">{formatPercent(item.share_revenue_percent)} выручки</div>
											<b style={{ color: hasPlan ? color : "var(--dark-text-muted, #8791a1)" }}>{hasPlan ? formatPercent(item.execution_percent) : "—"}</b>
										</div>
									</div>
									<ProgressLine percent={item.execution_percent} color={color} />
								</div>
							)
						})}
					</SectionCard>

					<div className="row">
						<div className="col-xl-6">
							<SectionCard title="Выручка по дням" titleIcon={<span className="text-success">↗</span>} subtitle="Текущая неделя против прошлой">
								<ReactApexChart options={revenueTrendChart.options} series={revenueTrendChart.series} type="area" height={300} />
							</SectionCard>
						</div>
						<div className="col-xl-6">
							<SectionCard title="Чеки по дням" titleIcon={<span className="text-primary">↗</span>} subtitle="Текущая неделя против прошлой">
								<ReactApexChart options={chequesTrendChart.options} series={chequesTrendChart.series} type="bar" height={300} />
							</SectionCard>
						</div>
					</div>
				</div>
			)}

			{activeTab === "branches" && (
				<div className="pb-3">
					<SectionCard title="План/факт по филиалам" subtitle="Итог по выручке — только по филиалам с планом">
						<BranchPlanList items={data?.actions?.branch_plan || []} />
					</SectionCard>

					<SectionCard title="Food Cost" subtitle="От товарной выручки после скидки чека (без обслуживания и доставки)">
						<div className="row g-3">
							<IndicatorCard title="Food Cost" value={foodCost.percent?.actual ? formatPercent(foodCost.percent.actual) : "—"} metric={foodCost.percent} inverse />
							<IndicatorCard title="Себестоимость" value={shortMetric(foodCost.cost_amount_short, metricActual(foodCost.cost_amount))} metric={foodCost.cost_amount} inverse />
							<IndicatorCard title="Товарная выручка" value={shortMetric(foodCost.product_revenue_short, metricActual(foodCost.product_revenue))} metric={foodCost.product_revenue} />
							<IndicatorCard title="Валовая прибыль" value={shortMetric(foodCost.gross_profit_short, metricActual(foodCost.gross_profit))} metric={foodCost.gross_profit} />
						</div>
					</SectionCard>

					<SectionCard title="Рейтинг филиалов" subtitle="Все филиалы с продажами за неделю или неделю сравнения">
						<SimpleTable
							columns={[
								{ key: "rank", label: "#", render: (_, index) => <RankBadge rank={index + 1} /> },
								{ key: "pos_name", label: "Филиал", render: row => <b>{row.pos_name}</b> },
								{ key: "revenue", label: "Выручка", className: "text-end", render: row => shortMetric(row.revenue_short, row.revenue) },
								{ key: "revenue_growth_percent", label: "Выручка %", className: "text-end", render: row => <GrowthValue value={row.revenue_growth_percent} /> },
								{ key: "cheques", label: "Чеки", className: "text-end", render: row => formatMoney(row.cheques) },
								{ key: "cheques_growth_percent", label: "Чеки %", className: "text-end", render: row => <GrowthValue value={row.cheques_growth_percent} /> },
								{ key: "margin_percent", label: "Маржа", className: "text-end", render: row => (
									<div>
										<b>{row.margin_percent === null ? "—" : formatPercent(row.margin_percent)}</b>
										<div><GrowthValue value={row.margin_growth_percent} unit="pp" /></div>
									</div>
								) },
							]}
							rows={data?.branches?.data || []}
						/>
					</SectionCard>

					<SectionCard title="Топ курьеров">
						<CouriersTable rows={data?.actions?.top_couriers || []} />
					</SectionCard>
				</div>
			)}

			{activeTab === "sales" && (
				<div className="pb-3">
					<SectionCard title="Показатели продаж" titleIcon={<i className="uil uil-chart-bar"></i>}>
						<div className="row g-3">
							<IndicatorCard title="Продано блюд" value={formatMetric(metricActual(indicators.dishes_sold))} metric={indicators.dishes_sold} />
							<IndicatorCard title="Доля топ-1" value={formatPercent(indicators.top_1_share_percent)} subtitle="товарной выручки у топ блюда" />
							<IndicatorCard title="Чеки в день" value={formatMoney(metricActual(indicators.cheques_per_day), 1)} metric={indicators.cheques_per_day} />
							<IndicatorCard title="Доля комбо" value={formatPercent(indicators.combo_share_percent)} subtitle="по количеству позиций" />
						</div>
					</SectionCard>

					<div className="row">
						<div className="col-xl-6">
							<SectionCard title="Топ блюд" titleIcon={<i className="uil uil-trophy"></i>}>
								<SimpleTable
									columns={[
										{ key: "rank", label: "#", render: row => <RankBadge rank={row.rank} /> },
										{ key: "product_name", label: "Название", render: row => <b>{row.product_name}</b> },
										{ key: "revenue", label: "Выручка", className: "text-end", render: row => <>{shortMetric(row.revenue_short, row.revenue)} <GrowthValue value={row.revenue_growth_percent} /></> },
										{ key: "quantity", label: "Кол-во", className: "text-end", render: row => formatMoney(row.quantity) },
									]}
									rows={data?.sales?.top_products || []}
								/>
							</SectionCard>
						</div>
						<div className="col-xl-6">
							<SectionCard title="Антирейтинг" titleIcon={<span className="text-danger">↘</span>} subtitle="Сильнейшее падение выручки к прошлой неделе">
								<SimpleTable
									columns={[
										{ key: "rank", label: "#", render: row => <RankBadge rank={row.rank} /> },
										{ key: "product_name", label: "Название", render: row => <b>{row.product_name}</b> },
										{ key: "revenue", label: "Выручка", className: "text-end", render: row => <>{shortMetric(row.revenue_short, row.revenue)} <GrowthValue value={row.revenue_growth_percent} /></> },
										{ key: "quantity", label: "Кол-во", className: "text-end", render: row => formatMoney(row.quantity) },
									]}
									rows={data?.sales?.anti_rating || []}
								/>
							</SectionCard>
						</div>
					</div>

					<SectionCard title="Группы блюд" titleIcon={<i className="uil uil-layer-group"></i>}>
						<ProductGroupsTable rows={data?.sales?.groups || []} openedGroups={openedGroups} toggleGroup={toggleGroup} className="mb-3" />
					</SectionCard>
				</div>
			)}

			{activeTab === "actions" && (
				<div className="pb-3">
					<SectionCard title="Герои недели" titleIcon={<i className="uil uil-trophy"></i>}>
						<div className="row g-3">
							{data?.actions?.heroes?.map(item => (
								<div className="col-xl-4 col-md-6" key={item.employee_login}>
									<div className="border rounded-3 p-3 text-center">
										<div className="rounded-circle bg-light mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: "96px", height: "96px", fontSize: "32px" }}>
											{String(item.employee_name || "?").slice(0, 1)}
										</div>
										<h5>{item.employee_name}</h5>
										<div className="fw-bold"><GrowthValue value={item.revenue_growth_percent} /></div>
										<div className="text-muted small">Выручка {shortMetric(item.revenue_short, item.revenue)} · Чеки {formatMoney(item.cheques)}</div>
									</div>
								</div>
							))}
							{!data?.actions?.heroes?.length && <div className="text-muted">Нет данных</div>}
						</div>
					</SectionCard>

					<SectionCard title="Топ курьеров">
						<CouriersTable rows={data?.actions?.top_couriers || []} />
					</SectionCard>

					<SectionCard title="Рейтинг сотрудников" subtitle="Официант чека, а если его нет — кассир">
						<SimpleTable
							columns={[
								{ key: "rank", label: "#", render: row => <RankBadge rank={row.rank} /> },
								{ key: "employee_name", label: "Сотрудник", render: row => <b>{row.employee_name}</b> },
								{ key: "revenue", label: "Выручка", className: "text-end", render: row => shortMetric(row.revenue_short, row.revenue) },
								{ key: "revenue_growth_percent", label: "Δ", className: "text-end", render: row => <GrowthValue value={row.revenue_growth_percent} /> },
								{ key: "cheques", label: "Чеки", className: "text-end", render: row => formatMoney(row.cheques) },
								{ key: "average_cheque", label: "Ср. чек", className: "text-end", render: row => formatMetric(row.average_cheque) },
							]}
							rows={data?.actions?.employee_rating || []}
							className="mb-3"
						/>
					</SectionCard>
				</div>
			)}
		</>
	)
}

export default Weekly
